import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Camera, Check, Minus, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Input, Select, Textarea } from "@/components/ui";
import { PhotoCaptureButton } from "@/features/photos/PhotoCaptureButton";
import { SignaturePad, type SignaturePadHandle } from "@/features/signature/SignaturePad";
import type { FieldDef, FormValues } from "./schema";
import type { GpsFix } from "@/features/gps/useGps";
import type { WatermarkContext } from "@/features/photos/capture";

// Renders one field. Stateless — takes value + onChange and does the right
// thing per kind. Pickers live in this file too so the field table stays
// together and we don't spray lookups everywhere.

interface Props {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  gps: GpsFix | null;
  ctx: WatermarkContext;
  signatureRefs?: React.MutableRefObject<Record<string, SignaturePadHandle | null>>;
  autoFocus?: boolean;
}

export function FormField({ field, value, onChange, error, gps, ctx, signatureRefs, autoFocus }: Props) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text mb-1.5">
        {field.label}
        {field.required && <span className="text-accent ml-1">*</span>}
      </label>
      {field.help && <div className="text-[11px] text-text-muted mb-2">{field.help}</div>}
      <FieldInner
        field={field}
        value={value}
        onChange={onChange}
        gps={gps}
        ctx={ctx}
        signatureRefs={signatureRefs}
        autoFocus={autoFocus}
      />
      {error && <div className="text-[11px] text-danger mt-1.5">{error}</div>}
    </div>
  );
}

function FieldInner({ field, value, onChange, gps, ctx, signatureRefs, autoFocus }: Props) {
  switch (field.type) {
    case "text":
      return <Input type="text" value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} placeholder={field.placeholder} autoFocus={autoFocus} />;
    case "textarea":
      return <Textarea rows={field.rows ?? 4} value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} placeholder={field.placeholder} autoFocus={autoFocus} />;
    case "number":
      return <Input type="number" mono min={field.min} max={field.max} step={field.step} value={(value as number | string) ?? ""} onChange={e => onChange(e.target.value)} autoFocus={autoFocus} />;
    case "date":
      return <Input type="date" mono value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />;
    case "datetime":
      return <Input type="datetime-local" mono value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />;
    case "time":
      return <Input type="time" mono value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />;

    case "select":
      return (
        <Select value={(value as string) ?? ""} onChange={e => onChange(e.target.value || null)}>
          <option value="">— select —</option>
          {(field.options ?? []).map((o) => {
            const { val, lbl } = typeof o === "string" ? { val: o, lbl: o } : { val: o.value, lbl: o.label };
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </Select>
      );

    case "multi":
      return <MultiSelect options={field.options ?? []} value={(value as string[]) ?? []} onChange={onChange as (v: string[]) => void} />;

    case "yesno":
      return <YesNo value={value as boolean | null} onChange={onChange} />;
    case "yesno_na":
      return <YesNo value={value as boolean | "na" | null} onChange={onChange} withNa />;

    case "site_picker":
      return <SitePicker value={value as string | null} onChange={onChange as (v: string | null) => void} />;
    case "project_picker":
      return <ProjectPicker value={value as string | null} onChange={onChange as (v: string | null) => void} />;
    case "employee_picker":
      return <EmployeePicker value={value as string | null} onChange={onChange as (v: string | null) => void} />;
    case "swms_multi":
      return <SwmsPicker value={(value as string[]) ?? []} onChange={onChange as (v: string[]) => void} />;

    case "photo":
      return <PhotoField value={(value as unknown[]) ?? []} onChange={onChange as (v: unknown[]) => void} field={field} gps={gps} ctx={ctx} />;

    case "signature":
      return (
        <SignaturePad
          ref={(r) => { if (signatureRefs) signatureRefs.current[field.key] = r; }}
          onFirstStroke={() => onChange({ drawn: true })}
        />
      );

    default:
      return <Input type="text" value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />;
  }
}

// -----------------------------------------------------------------------------
// Multi-select (tag-style)
// -----------------------------------------------------------------------------
function MultiSelect({ options, value, onChange }: { options: FieldDef["options"] extends infer O ? O : never; value: string[]; onChange: (v: string[]) => void }) {
  const normalised = (options ?? []).map(o => typeof o === "string" ? { value: o, label: o } : o);
  return (
    <div className="flex flex-wrap gap-1.5">
      {normalised.map(o => {
        const active = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(active ? value.filter(v => v !== o.value) : [...value, o.value])}
            className={`px-3 py-1.5 rounded text-[12px] font-mono border transition-colors ${
              active
                ? "bg-accent/12 border-accent/30 text-accent"
                : "bg-ink-raised border-line text-text-muted hover:text-text"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Yes / No (/ N.A.) — big tap targets
// -----------------------------------------------------------------------------
function YesNo({ value, onChange, withNa = false }: { value: boolean | "na" | null | undefined; onChange: (v: unknown) => void; withNa?: boolean }) {
  const opts: { key: boolean | "na"; label: string; icon: React.ReactNode; active: string }[] = [
    { key: true,  label: "Yes", icon: <Check size={14} />,  active: "bg-ok/20 border-ok/40 text-ok" },
    { key: false, label: "No",  icon: <X size={14} />,      active: "bg-danger/20 border-danger/40 text-danger" },
  ];
  if (withNa) opts.push({ key: "na", label: "N/A", icon: <Minus size={14} />, active: "bg-text-muted/20 border-text-muted/40 text-text-muted" });
  return (
    <div className="grid grid-cols-3 gap-2" style={{ gridTemplateColumns: `repeat(${opts.length}, minmax(0, 1fr))` }}>
      {opts.map(o => {
        const active = value === o.key;
        return (
          <button
            key={String(o.key)}
            type="button"
            onClick={() => onChange(o.key)}
            className={`flex items-center justify-center gap-2 py-3 rounded font-medium uppercase tracking-wider2 text-[12px] border transition-colors ${
              active ? o.active : "bg-ink-raised border-line text-text-muted hover:text-text"
            }`}
          >
            {o.icon} {o.label}
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pickers — query Supabase live
// -----------------------------------------------------------------------------
function SitePicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { data = [] } = useQuery({
    queryKey: ["sites_for_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sites")
        .select("site_id, site_code, site_name, suburb, state")
        .eq("is_active", true)
        .order("site_code")
        .limit(500);
      if (error) throw error;
      return data;
    },
  });
  return (
    <Select value={value ?? ""} onChange={e => onChange(e.target.value || null)}>
      <option value="">— select site —</option>
      {data.map((s: any) => (
        <option key={s.site_id} value={s.site_id}>
          {s.site_name}{s.suburb ? ` — ${s.suburb}` : ""}{s.site_code ? ` · ${s.site_code}` : ""}
        </option>
      ))}
    </Select>
  );
}

function ProjectPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { data = [] } = useQuery({
    queryKey: ["projects_for_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name")
        .in("status", ["live", "mobilising", "acceptance_pending"])
        .order("project_code")
        .limit(500);
      if (error) throw error;
      return data;
    },
  });
  return (
    <Select value={value ?? ""} onChange={e => onChange(e.target.value || null)}>
      <option value="">— select project —</option>
      {data.map((p: any) => (
        <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>
      ))}
    </Select>
  );
}

function EmployeePicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { data = [] } = useQuery({
    queryKey: ["employees_for_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_names")
        .select("employee_id, full_name, is_active")
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return data;
    },
  });
  return (
    <Select value={value ?? ""} onChange={e => onChange(e.target.value || null)}>
      <option value="">— select person —</option>
      {data.map((e: any) => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
    </Select>
  );
}

function SwmsPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { data = [] } = useQuery({
    queryKey: ["swms_for_picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("swms_library")
        .select("swms_id, swms_code, display_name, is_active")
        .eq("is_active", true)
        .order("swms_code");
      if (error) throw error;
      return data;
    },
  });
  return (
    <div className="space-y-1.5">
      {data.map((s: any) => {
        const active = value.includes(s.swms_id);
        return (
          <button
            key={s.swms_id}
            type="button"
            onClick={() => onChange(active ? value.filter(v => v !== s.swms_id) : [...value, s.swms_id])}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded border text-left transition-colors ${
              active ? "bg-accent/12 border-accent/30" : "bg-ink-raised border-line hover:border-line-light"
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${active ? "bg-accent border-accent" : "border-line-light"}`}>
              {active && <Check size={12} className="text-black" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-text">{s.display_name}</div>
              <div className="text-[11px] font-mono text-text-muted">{s.swms_code}</div>
            </div>
          </button>
        );
      })}
      {data.length === 0 && <div className="text-[12px] text-text-muted p-4 text-center">No SWMS in library yet.</div>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Photo field — captures 1+ photos inline with the form
// -----------------------------------------------------------------------------
function PhotoField({ value, onChange, field, gps, ctx }: { value: unknown[]; onChange: (v: unknown[]) => void; field: FieldDef; gps: GpsFix | null; ctx: WatermarkContext }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <PhotoCaptureButton
          label={`Add photo${field.max_photos && field.max_photos > 1 ? "s" : ""}`}
          multiple={(field.max_photos ?? 1) > 1}
          ctx={ctx}
          gps={gps}
          onCaptured={(p) => onChange([...value, p])}
          disabled={field.max_photos != null && value.length >= field.max_photos}
          size="sm"
        />
        <span className="text-[11px] font-mono text-text-muted">
          {value.length}{field.max_photos ? ` / ${field.max_photos}` : ""} captured
        </span>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((p: any, i) => (
            <div key={i} className="relative w-20 h-20 rounded bg-ink-raised border border-line flex items-center justify-center overflow-hidden">
              {p.watermarked_url ? (
                <img src={p.watermarked_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Camera size={20} className="text-text-muted" />
              )}
              {p.queued_offline && (
                <div className="absolute bottom-0 inset-x-0 text-[9px] text-center font-mono uppercase tracking-wider2 bg-warn/30 text-warn py-0.5">Queued</div>
              )}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center text-xs"
                title="Remove"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
